import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { MouseEventHandler, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { AnnotationType, getAnnotationNameProperty, getAnnotationVisibleProperty } from '@pointorama/potree-shared';
import { ContextMenu } from '../../../../components/ContextMenu';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { useDeleteConfirmation, type DeletableType } from '../../../../contexts/DeleteConfirmationContext';
import { useIsMultiSelect } from '../../../../hooks/modules/renderer/useIsMultiSelect';
import { useRename } from '../../../../hooks/modules/renderer/useRename';
import { useRenameSubmit } from '../../../../hooks/modules/renderer/useRenameSubmit';
import { useDeleteAnnotation } from '../../../../hooks/potree/useDeleteAnnotation';
import { useAnnotationLookup } from '../../../../hooks/potree/useAnnotationContext';
import { useAnnotationById } from '../../../../hooks/potree/useAnnotationStore';
import { useCustomSelector } from '../../../../redux/store';
import { usePropertyValue } from '../../../Properties/reactive/usePropertyValue';
import { T } from '../../../../translation/src';
import { Annotation as TypeAnnotation } from '../../../../types/graphqlTypes';
import { AnnotationsEyeIcon, AnnotationsFocusIcon } from './AnnotationsIconsWrapper';
import { useAnnotationIcon } from '../../../../utils/annotationIcons';
import type { AnnotationProps } from './Annotation';

interface OpeningContextMenuItemsProps {
  identifier: string;
  isMultiSelect: boolean;
  onToggleNameEditable?: () => void;
}

const OpeningContextMenuItems: React.FC<OpeningContextMenuItemsProps> = ({ identifier, isMultiSelect, onToggleNameEditable }) => {
  const { deleteAnnotation, deleteSelected } = useDeleteAnnotation();
  const { selectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const annotationsByIdentifier = useAnnotationLookup();
  const { requestDeleteConfirmation, requiresConfirmation } = useDeleteConfirmation();

  const onDelete = useCallback(() => {
    if (isMultiSelect) {
      const needsConfirmation = selectedAnnotations.some((selected) => {
        const annotation = annotationsByIdentifier[selected.identifier];
        return requiresConfirmation(annotation?.type as DeletableType | undefined);
      });
      const run = () => deleteSelected(selectedAnnotations);
      if (needsConfirmation) requestDeleteConfirmation(run);
      else run();
      return;
    }
    const run = () => deleteAnnotation(identifier);
    if (requiresConfirmation(AnnotationType.OPENING)) requestDeleteConfirmation(run);
    else run();
  }, [isMultiSelect, selectedAnnotations, annotationsByIdentifier, deleteSelected, deleteAnnotation, identifier, requestDeleteConfirmation, requiresConfirmation]);

  return (
    <>
      <div className="flex flex-col">
        <SelectItemContent icon={PencilIcon} onClick={onToggleNameEditable}>
          <T _str="rename" swc />
        </SelectItemContent>
      </div>
      <div className="flex flex-col">
        <SelectItemContent icon={TrashIcon} onClick={onDelete}>
          <T _str="delete" swc />
        </SelectItemContent>
      </div>
    </>
  );
};

export interface OpeningRowProps {
  identifier: string;
  /** Identifier of the parent room — only used to disambiguate React keys at the call site. */
  roomIdentifier: string;
  isReadOnly: boolean;
  onClickAnnotation: AnnotationProps['onClickAnnotation'];
  /** Indent class chosen by the parent so openings sit one level deeper than their room. */
  className?: string;
}

/** A nested opening row rendered under a room. Selectable, renamable, with
 *  functional eye + focus icons, but never draggable. Skips useSortable so
 *  the same opening can appear under multiple rooms without colliding in
 *  the dnd-kit SortableContext. */
const OpeningRow_: React.FC2<OpeningRowProps> = ({ identifier, roomIdentifier, isReadOnly, onClickAnnotation, className = 'pl-16' }) => {
  const annotation = useAnnotationById(identifier);
  const icon = useAnnotationIcon({ type: 'OPENING', identifier });
  const reactiveName = usePropertyValue(getAnnotationNameProperty(identifier));
  const reactiveVisible = usePropertyValue(getAnnotationVisibleProperty(identifier));

  const active = useSelector((state: { rendererProvider: { selectedAnnotations: { identifier: string }[] } }) => state.rendererProvider.selectedAnnotations.some((a) => a.identifier === identifier));

  const { selectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const isMultiSelect = useIsMultiSelect() && selectedAnnotations.some((selected) => selected.identifier === identifier);

  const submitRename = useRenameSubmit();
  const [{ onToggleNameEditable }, { nameEditable, nameInputComponent }] = useRename({
    name: reactiveName,
    annotationIdentifier: identifier,
    instanceKey: `${roomIdentifier}-${identifier}`,
  });

  const onClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      submitRename();
      onClickAnnotation({ identifier, type: 'annotation', instanceKey: roomIdentifier }, { isCtrl: event.ctrlKey || event.metaKey, isShift: event.shiftKey });
    },
    [onClickAnnotation, identifier, roomIdentifier, submitRename],
  );

  if (!annotation) return null;
  if (nameEditable) return nameInputComponent || null;

  return (
    <ContextMenu
      popoverMenuDisabled={isReadOnly}
      buttonChild={
        <div data-annotation-id={identifier} className="w-full">
          <SelectItemContent onClick={onClick} icon={icon} active={active} selectStyle="primary" idle={!reactiveVisible} className={classNames(className)} onDoubleClick={onToggleNameEditable}>
            <div className="overflow-hidden">{reactiveName}</div>
            <div className="mx-2 flex justify-end grow" />
            <div className="flex justify-end">
              <AnnotationsFocusIcon identifier={identifier} visible={reactiveVisible} annotation={annotation as TypeAnnotation} />
              <AnnotationsEyeIcon identifier={identifier} visible={reactiveVisible} annotation={annotation as TypeAnnotation} />
            </div>
          </SelectItemContent>
        </div>
      }
      panelClassName="divide-y"
    >
      <OpeningContextMenuItems identifier={identifier} isMultiSelect={isMultiSelect} onToggleNameEditable={onToggleNameEditable} />
    </ContextMenu>
  );
};

export const OpeningRow = memo(OpeningRow_);
